// src/pages/PrivacyPolicy.jsx
import React from "react";
import { motion } from "framer-motion";

export default function PrivacyPolicy() {
  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-900 py-10 sm:py-12 px-3 sm:px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Card wrapper to match site style + phone friendly spacing */}
        <article className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border-t-4 border-blue-600 p-5 sm:p-8 space-y-6 sm:space-y-7">
          <header className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-blue-800 dark:text-slate-100 leading-tight">
              🔒 Coinzetto Privacy Policy
            </h1>
            <p className="text-[12px] sm:text-sm text-blue-600 dark:text-slate-300">
              Effective Date: 23 May 2025 | Last Updated: 5th July 2025
            </p>
          </header>

          <section className="space-y-3 sm:space-y-4">
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              Coinzetto Technologies Private Limited (“Coinzetto”, “we”, “our”, “us”) respects your privacy and is committed to protecting your personal data. This Privacy Policy outlines:
            </p>
            <ul className="list-disc list-outside pl-5 sm:pl-6 text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 space-y-1.5">
              <li>What personal information we collect</li>
              <li>How we collect and use it</li>
              <li>With whom we may share it</li>
              <li>How you can access and update it</li>
              <li>Security measures to protect your data</li>
              <li>Your rights</li>
              <li>How to contact us</li>
            </ul>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              By accessing or using Coinzetto’s website, mobile app, APIs, or any services provided (collectively, the “Coinzetto Platform”), you agree to this Privacy Policy.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              1. What is Personal Information?
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              "Personal Information" refers to any data or information that directly or indirectly identifies you, including your name, contact details, KYC, financial info, and more.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              2. What Information Do We Collect?
            </h2>
            <ul className="list-disc list-outside pl-5 sm:pl-6 text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 space-y-1.5">
              <li>Identity and contact details</li>
              <li>Login credentials, device data, and usage logs</li>
              <li>Banking and wallet info</li>
              <li>Feedback and support interactions</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              3. How Do We Collect Your Information?
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              We collect info directly, automatically via your browser/app, or from third-party integrations.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              4. When Do We Collect Your Information?
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              At registration, during KYC, while transacting, or using our services.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              5. Purpose of Collection and Use
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              For account management, legal compliance, transaction facilitation, fraud detection, customer support, and communication.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              6. Cookies and Tracking
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              We use cookies to maintain sessions, analyze behavior, and personalize the platform. You may disable cookies in your browser.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              7. Information Sharing and Disclosure
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              Your data may be shared with internal teams, legal authorities, or trusted third parties under strict confidentiality.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              8. Data Security
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              We implement SSL, 2FA, access control, and other security measures to protect your information.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              9. Your Rights
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              You can request access, correction, or deletion of your personal data, or raise grievances about processing.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              10. Data Retention
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              We retain your data as long as your account is active and up to 5 years thereafter for compliance.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              11. Third-Party Links
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              Coinzetto is not responsible for third-party privacy practices. Review their terms separately.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              12. Grievances and Contact
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              For privacy concerns, contact our Grievance Officer:<br />
              <span className="block mt-1">
                Email:{" "}
                <a
                  href="mailto:admin@coinzetto.com"
                  className="underline underline-offset-2 text-blue-700 dark:text-blue-300"
                >
                  admin@coinzetto.com
                </a>
              </span>
              <span className="block">Phone: +91 8619 03 8619</span>
              <span className="block">
                Address: 4 Ram Nagar colony, Sundar Nagar, Jaisinghpura Khor, Jaipur, 302027
              </span>
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100">
              13. Changes to This Policy
            </h2>
            <p className="text-[13px] sm:text-base text-blue-800/90 dark:text-slate-200 leading-relaxed">
              We may update this policy occasionally. Continued use after changes implies acceptance.
            </p>
          </section>
        </article>
      </div>
    </motion.div>
  );
}
